/**
 * A confirmation modal for archiving assignments
 **/
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";

interface Props {
    isArchiving: boolean;
    assignmentIds: Array<string>;
    onClose: () => void;
    archiveAssignments: (ids: Array<string>) => void;
    bodyText: React.ReactNode;
    title: string;
    buttonLabel?: string;
}

const ArchiveAssignmentsConfirmationModal = (props: Props): React.ReactNode => {
    const {
        assignmentIds,
        onClose,
        isArchiving,
        bodyText,
        title,
        buttonLabel,
        archiveAssignments,
    } = props;

    const [opened, setOpened] = React.useState(true);

    const handleClose = React.useCallback(() => {
        setOpened(false);
        onClose();
    }, [onClose]);

    const handleArchiveAssignments = React.useCallback(() => {
        archiveAssignments(assignmentIds);
    }, [archiveAssignments, assignmentIds]);

    const num = assignmentIds.length;

    const defaultButtonLabel = t`Yes, delete (${num})`;

    return (
        <ModalLauncher
            testId="archive-assignments-confirmation-modal"
            opened={opened}
            onClose={handleClose}
            modal={
                <OnePaneDialog
                    title={title}
                    style={styles.modal}
                    content={
                        <Body style={{maxInlineSize: 256}}>{bodyText}</Body>
                    }
                    footer={
                        <View
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-end",
                                gap: spacing.medium_16,
                            }}
                        >
                            <Button
                                testId="cancel-archive-assignments"
                                kind="tertiary"
                                onClick={handleClose}
                            >
                                {t`Cancel`}
                            </Button>
                            <Button
                                testId="confirm-archive-assignments"
                                onClick={handleArchiveAssignments}
                                disabled={isArchiving}
                                actionType="destructive"
                                style={{minInlineSize: 128}}
                            >
                                {isArchiving
                                    ? t`Deleting...`
                                    : buttonLabel || defaultButtonLabel}
                            </Button>
                        </View>
                    }
                />
            }
        />
    );
};

const styles = StyleSheet.create({
    modal: {
        width: 320,
        height: "fit-content",
    },
});

export default ArchiveAssignmentsConfirmationModal;
